// R · Card done (QR1_Reward; beats QR2_Streak, QR3_Perfect, QR4_Level) from state.reward (the Reward the server built):
// card -> streak (only when today got lit) -> perfect (only on a perfect day) -> level (only on a level-up, with the
// route stop it opened). Each beat is a fixed 390-wide design box scaled to fit (Fit). Leaving the last beat marks the
// reward seen (POST /api/reward/seen). Share opens SH and Back returns to the same beat (rbeat).
import { useEffect, useState } from 'preact/hooks';
import type { Reward } from '@shared/types';
import { C } from '@shared/content/characters';
import { LINES } from '@shared/content/lines';
import { COMING_SOON_LABEL, flameScale, stampDate, taskShort } from '@shared/rules';
import { useApp, rewardSeen } from '../../store';
import { go, replace, setParams, useParams } from '../../router';
import { haptic } from '../../native';
import { Screen, XScene, XCard, XFace, XKeep, Icon, Fit, Confetti, PrimaryButton, type IconName } from '../../components';
import { say } from '../../lib/say';
import { DAY_ONE } from '../../content/onboarding';
import { dayOf } from './util';
import s from './RewardScreen.module.css';

type Beat = 'card' | 'streak' | 'perfect' | 'level';
export const beatsOf = (rw: Reward): Beat[] => {
  const b: Beat[] = ['card'];
  if (rw.streakTo) b.push('streak');
  if (rw.perfect) b.push('perfect');
  if (rw.levelTo > rw.levelFrom) b.push('level');
  return b;
};

export function RewardScreen() {
  const st = useApp((x) => x.state);
  const params = useParams();
  const rw = st?.reward || null;
  const beats = rw ? beatsOf(rw) : ['card' as Beat];
  const [bi, setBi] = useState(() => Math.max(0, Math.min(beats.length - 1, params.rbeat ?? 0)));
  // Back to where the card was finished (phone v2): the chat for a tick there or a Working together started there.
  const home = params.from === 'C' ? 'C' : 'T';

  useEffect(() => { if (st && !rw) replace(home); }, [!!rw]);
  useEffect(() => { haptic(bi === 0 ? 'success' : 'medium'); }, [bi]);
  if (!st || !rw) return null;

  const ch = st.ch;
  const c = C[ch];
  const L = LINES[ch];
  const beat = beats[Math.min(bi, beats.length - 1)];
  const last = bi >= beats.length - 1;
  const date = stampDate(dayOf(rw.doneAt, st.user.tz));
  const k = rw.streakTo || st.streak.count || 1;
  const sv = { n: c.name, task: taskShort(rw.title), year: st.today.slice(0, 4), k };
  const line = rw.line || say(L.signature, sv);
  // Day 1 of a streak has its own line ("1 days in a row" read wrong). With moved cards, a perfect day never says every
  // card got done.
  const streakLine = k === 1 ? DAY_ONE[ch] : say(L.streak, sv);
  const moved = st.plan.cards.some((x) => x.st === 'moved');

  const finish = (to: () => void) => { void rewardSeen(); to(); };
  const next = () => { if (last) finish(() => go(home, { rbeat: null })); else { setParams({ rbeat: bi + 1 }); setBi(bi + 1); } };
  // Back from SH returns to this beat: keep it in R's own params before leaving.
  const share = () => { setParams({ rbeat: bi }); go('SH', { rbeat: bi, mode: beat === 'streak' ? 'streak' : 'card', from: 'R' }); };

  // Unlock panel (third round: XKeep art / a small scene of the new stage / an icon).
  const u = rw.unlock;
  const uType = u ? u.type : 'season';
  const uLabel = u ? u.label : COMING_SOON_LABEL;
  const uKind = ({ keep: 'NEW KEEPSAKE', chap: 'NEW CHAPTER', place: 'NEW LOOK', sound: 'NEW SOUND', season: 'COMING NEXT' } as Record<string, string>)[uType];
  const uSub = ({
    keep: `It goes on your side of ${c.placeName}.`, chap: 'Read it now, or later in Us.', place: `${c.placeName} just changed. Go take a look.`,
    sound: 'It plays next time you work together.', season: 'More is on the way.',
  } as Record<string, string>)[uType];
  const uIcon: IconName = uType === 'chap' ? 'book' : uType === 'sound' ? 'music' : 'star';
  const seeLabel = uType === 'chap' ? 'Read it' : uType === 'place' ? 'See it' : 'See it in Us';
  const seeIt = () => finish(() => (uType === 'chap' && u?.chap ? go('U2', { chap: u.chap, rbeat: null }) : go('U1', { rbeat: null })));

  const flameNext = rw.flameGrew ? 'Your flame just got bigger.' : rw.nextFlameIn ? `${rw.nextFlameIn} more ${rw.nextFlameIn === 1 ? 'day' : 'days'} to a bigger flame` : '';
  const fk = flameScale(k);
  const glow = fk >= 1.1 ? 36 : fk >= 1 ? 28 : fk >= 0.9 ? 20 : 14;
  const week = st.streak.week;
  const litDays = week.filter((d) => d.mark === 'lit' || d.day === st.today).length;
  const kept = week.filter((d) => d.mark === 'freeze' || d.mark === 'rest').length;
  const safeNote = st.streak.safeNote || null;

  return (
    <Screen theme={ch} id="R" bg="#0A0908" label={beat === 'card' ? 'Card done' : beat === 'streak' ? `${k}-day streak` : beat === 'perfect' ? 'Perfect day' : `Level ${rw.levelTo}`}>
      {beat === 'card' && <XScene ch={ch} stage={st.buddy.stage} mode="spot" video={false} alt="" />}
      {beat !== 'card' && <div class={s.glow} aria-hidden="true" data-beat={beat} />}
      {(beat === 'card' || beat === 'perfect') && <Confetti key={beat} />}

      <Fit w={390} h={beat === 'card' ? 576 : 580} min={0.6} class={s.fit}>
        {beat === 'card' && (
          <div class={s.box} key="card">
            <h1 class={s.cardH1}>Card done!</h1>
            <div class={s.flip}>
              <XCard ch={ch} face="back" w={300} h={344} fit="none" label={`DONE · ${date}`} line={line} note={rw.title.toUpperCase()}
                stamp="DONE" stampSub={date} e={2} tilt={-1.5} stampAnim="xmStamp 650ms 600ms cubic-bezier(.2,.9,.3,1.2) both" />
            </div>
            <div class={s.tiles}>
              {[
                { label: 'POINTS', value: `+${rw.gained}`, color: 'var(--acc)', delay: '700ms' },
                // Ticked off without Start (reward.direct, F_Reward): no "minutes worked".
                ...(rw.direct ? [] : [{ label: 'TIME', value: `${rw.minutes} min`, color: '#7FC8A9', delay: '820ms' }]),
                { label: 'TODAY', value: `${rw.dayDone} of ${rw.dayTotal}`, color: '#9DB7F5', delay: rw.direct ? '820ms' : '940ms' },
              ].map((t) => (
                <div key={t.label} class={s.tile} style={{ borderColor: t.color, animationDelay: t.delay }}>
                  <p class={s.tileLabel} style={{ background: t.color }}>{t.label}</p>
                  <p class={s.tileValue} style={{ color: t.color }}>{t.value}</p>
                </div>
              ))}
            </div>
            <div class={s.bonus}>
              {rw.bonus.map((b) => (
                <span key={b.label} class={s.chip}><Icon name="star" size={14} color="none" fill="var(--acc)" /><span>{`${b.label} +${b.pts}`}</span></span>
              ))}
            </div>
          </div>
        )}

        {beat === 'streak' && (
          <div class={s.box} key="streak">
            <div class={s.flameWrap}>
              <div class={s.flame}>
                <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: 160, height: 170, transform: `scale(${fk})`, transformOrigin: '50% 70%', fill: 'var(--acc)', stroke: '#FFE7A8', strokeWidth: 0.6, strokeLinejoin: 'round', filter: `drop-shadow(0 0 ${glow}px var(--acc))` }}>
                  <path d="M12 3c1 3.5 5 5.5 5 10a5 5 0 0 1-10 0c0-2.2 1.2-3.6 2.4-4.6.2 1.6 1 2.6 2.1 2.9C11 9 11.2 6 12 3z" />
                </svg>
              </div>
            </div>
            <div class={s.count}>
              {rw.streakFrom != null && <span class={s.countOld} aria-hidden="true">{rw.streakFrom}</span>}
              <span class={s.countNew}>{k}</span>
            </div>
            <h1 class={s.streakH1}>{`${k}-day streak!`}</h1>
            {/* With a safe note (rest day / weekly freeze), it takes the generic sub line's room under the week row. */}
            {!safeNote && <p class={s.streakSub}>Do one card a day to keep it going.</p>}
            {flameNext && <p class={s.streakNext} style={safeNote ? { top: 322 } : undefined}>{flameNext}</p>}
            <div class={s.week} style={safeNote ? { top: 362 } : undefined} role="img" aria-label={`This week: ${litDays} ${litDays === 1 ? 'day' : 'days'} lit, including today${kept ? `, ${kept} kept safe by a rest day or the weekly freeze` : ''}`}>
              {week.map((d) => {
                const today = d.day === st.today;
                const mark = today ? 'lit' : d.mark;
                return (
                  <div key={d.day} class={s.wd}>
                    <span class={s.wdLabel} style={{ color: today ? 'var(--acc)' : '#CFC8BC' }}>{d.label}</span>
                    <span class={s.wdDot} data-mark={mark} data-lit={mark === 'lit' ? 'true' : 'false'} data-today={today ? 'true' : 'false'}>
                      {mark === 'freeze' ? <Icon name="shield" size={18} strokeWidth={2.2} color="#9DB7F5" />
                        : mark === 'rest' ? <Icon name="moon" size={18} strokeWidth={2.2} color="#CFC8BC" />
                        : <Icon name="check" size={18} strokeWidth={3} color={mark === 'lit' ? 'var(--on)' : 'transparent'} />}
                    </span>
                  </div>
                );
              })}
            </div>
            {safeNote && <p class={s.safeNote}>{safeNote}</p>}
            <div class={s.say} style={{ top: 508 }}>
              <XFace ch={ch} e={2} s={44} ring="var(--acc)" alt="" />
              <p class={s.sayText}><span class="xm-sr-only">{c.name}: </span>{streakLine}</p>
            </div>
          </div>
        )}

        {beat === 'perfect' && (
          <div class={s.box} key="perfect">
            <div class={s.gift}>
              <div class={s.giftShake}><Icon name="gift" size={132} strokeWidth={1.4} color="var(--acc)" fill="rgba(255,255,255,0.06)" style={{ filter: 'drop-shadow(0 0 18px var(--acc))' }} /></div>
            </div>
            <h1 class={s.perfectH1}>Perfect day!</h1>
            <p class={s.perfectSub}>{moved ? 'Every card is done or moved to tomorrow.' : 'Every card you planned is done.'}</p>
            <div class={s.plusRow}><span class={s.plus}>+30 bonus points</span></div>
            <div class={s.note}>
              <p class={s.noteHead}>{`A NOTE FROM ${c.name.toUpperCase()}`}</p>
              <p class={s.noteText}>{`${say(L.perfect, sv)} — ${c.name.charAt(0)}`}</p>
            </div>
          </div>
        )}

        {beat === 'level' && (
          <div class={s.box} key="level">
            <div class={s.burst} aria-hidden="true">
              {Array.from({ length: 12 }, (_, i) => <span key={i} class={s.ray} style={{ transform: `rotate(${i * 30}deg)` }} />)}
              <Icon name="star" size={100} color="#FFE7A8" fill="var(--acc)" strokeWidth={0.6} style={{ position: 'absolute', left: 50, top: 50, filter: 'drop-shadow(0 0 16px var(--acc))' }} />
            </div>
            <h1 class={s.levelH1}>{`Level ${rw.levelTo}!`}</h1>
            <div class={s.unlock}>
              {uType === 'keep' && u?.keep != null && <XKeep ch={ch} i={u.keep} s={80} />}
              {uType === 'place' && <div class={s.uScene}><XScene ch={ch} stage={u?.place || st.buddy.stage} mode="clear" w={80} h={124} video={false} drift={false} alt="" /></div>}
              {uType !== 'keep' && uType !== 'place' && <span class={s.uIcon} aria-hidden="true"><Icon name={uIcon} size={38} strokeWidth={1.8} color="var(--acc)" /></span>}
              <span class={s.uText}>
                <span class={s.uKind}>{uKind}</span>
                <span class={s.uLabel}>{uLabel}</span>
                <span class={s.uSub}>{uSub}</span>
              </span>
            </div>
            <div class={s.say} style={{ top: 464 }}>
              <XFace ch={ch} e={1} s={44} ring="var(--acc)" alt="" />
              <p class={s.sayText}><span class="xm-sr-only">{c.name}: </span>{say(L.levelUp, sv)}</p>
            </div>
          </div>
        )}
      </Fit>

      <div class={s.btns}>
        {beat === 'level' && <PrimaryButton h={58} onClick={seeIt} data-cta="see-it">{seeLabel}</PrimaryButton>}
        <div class={s.row}>
          {(beat === 'card' || beat === 'streak') && (
            <button type="button" class={s.share} onClick={share}><Icon name="share" size={18} /><span>Share</span></button>
          )}
          {beat === 'level'
            ? <button type="button" class={s.keepGoing} onClick={next}>{last ? 'Keep going' : 'Continue'}</button>
            : <PrimaryButton h={58} grow={1.6} onClick={next} data-cta="continue">Continue</PrimaryButton>}
        </div>
      </div>
    </Screen>
  );
}
